import type {
  PublicStatusBucketRecord,
  PublicStatusNamespaceRecord,
  PublicStatusResponse,
  PublicStatusState,
} from '@nadoutong/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { PlatformApiError } from '../../api/client.ts';
import { useNadoutongPublicStatus, usePlatformHealth } from '../../api/useStatus.ts';
import { formatTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  KpiCard,
  PageHeader,
  Panel,
  Skeleton,
  StatusLamp,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
  cx,
} from '../../ui/index.ts';
import type { ChipTone, LampTone } from '../../ui/index.ts';

/**
 * `/access/status` — the service-status surface as a full page
 * (epic #897 PR 5). Formerly a panel buried inside the overloaded Keys
 * page; the dashboard status strip links here.
 *
 * The overall-state KPI lamp is the ONE pulsing element on the page
 * (DESIGN.md §2 "glow only when live"). Refresh cadence is inherited
 * from `useStatus`'s staleTime — no extra polling here.
 *
 * #1112: uptime-timeline layout. When the payload carries the
 * `namespaces[]` rollup with `history` buckets, each namespace renders
 * as a timeline row (48 × 30-min blocks over 24h) with member-SKU mini
 * timelines behind a disclosure, and "recent events" derive from the
 * namespace history transitions (traffic buckets only) — no channel
 * names, no channel counts, no platform-wide request totals. Payloads
 * without `namespaces[]` fall back to the legacy vendor/channel view
 * (fallback removed in #916).
 */

type TranslateFn = ReturnType<typeof useT>;

const STATE_LAMP: Record<PublicStatusState, LampTone> = {
  healthy: 'ok',
  busy: 'warn',
  unavailable: 'bad',
};

const STATE_CHIP: Record<PublicStatusState, ChipTone> = {
  healthy: 'ok',
  busy: 'warn',
  unavailable: 'bad',
};

const TIMELINE_BLOCK: Record<PublicStatusState, string> = {
  healthy: 'bg-ok',
  busy: 'bg-warn',
  unavailable: 'bg-bad',
};

function stateText(state: PublicStatusState, t: TranslateFn): string {
  switch (state) {
    case 'healthy':
      return t('accessStatus.state.healthy');
    case 'busy':
      return t('accessStatus.state.busy');
    case 'unavailable':
      return t('accessStatus.state.unavailable');
  }
}

const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function formatBucketTime(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? TIME_FORMATTER.format(new Date(ms)) : '—';
}

/**
 * State transitions reconstructed from the namespace timelines. Only
 * buckets that carried traffic participate — a zero-request bucket is
 * "no data", not "recovered".
 */
interface DerivedEvent {
  readonly key: string;
  readonly namespaceDisplayName: string;
  readonly from: PublicStatusState;
  readonly to: PublicStatusState;
  readonly at: string;
}

function deriveNamespaceEvents(
  namespaces: readonly PublicStatusNamespaceRecord[],
): DerivedEvent[] {
  const events: DerivedEvent[] = [];
  for (const namespace of namespaces) {
    let previous: PublicStatusState | null = null;
    for (const bucket of namespace.history ?? []) {
      if (bucket.request_count === 0) continue;
      if (previous !== null && bucket.state !== previous) {
        events.push({
          key: `${namespace.namespace_id}:${bucket.bucket_start}`,
          namespaceDisplayName: namespace.namespace_display_name,
          from: previous,
          to: bucket.state,
          at: bucket.bucket_start,
        });
      }
      previous = bucket.state;
    }
  }
  return events.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 8);
}

export function AccessStatus(): JSX.Element {
  const t = useT();
  const statusQ = useNadoutongPublicStatus();
  const healthQ = usePlatformHealth();
  const status = statusQ.data;
  const namespaces = status?.namespaces ?? [];
  const hasNamespaces = namespaces.length > 0;

  return (
    <section data-testid="status-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('accessStatus.title')}
        sub={t('accessStatus.subtitle')}
        testId="status-header"
      />

      {statusQ.isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="status-loading">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : statusQ.isError ? (
        <div role="alert" data-testid="status-error" className="flex flex-wrap items-center gap-3">
          <p className="m-0 text-dense text-bad">
            {statusQ.error instanceof PlatformApiError
              ? `${statusQ.error.code}: ${statusQ.error.message}`
              : t('accessStatus.error')}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void statusQ.refetch();
            }}
            data-testid="status-retry"
          >
            {t('accessStatus.retry')}
          </Button>
        </div>
      ) : status !== undefined ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard
              testId="status-kpi-overall"
              label={t('accessStatus.kpi.overall')}
              lamp={STATE_LAMP[status.summary.state]}
              pulse
              tone={
                status.summary.state === 'healthy'
                  ? 'ok'
                  : status.summary.state === 'busy'
                    ? 'warn'
                    : 'bad'
              }
              value={stateText(status.summary.state, t)}
              delta={{
                text: hasNamespaces
                  ? t('accessStatus.kpi.overall_hint_ns', {
                      healthy: namespaces.filter(
                        (namespace) => (namespace.state ?? 'healthy') === 'healthy',
                      ).length,
                      total: namespaces.length,
                    })
                  : t('accessStatus.kpi.overall_hint', {
                      healthy: status.summary.healthy_channels,
                      total: status.summary.total_channels,
                    }),
                tone: 'neutral',
              }}
            />
            <HealthKpi healthQ={healthQ} />
            <KpiCard
              testId="status-kpi-updated"
              label={t('accessStatus.kpi.updated')}
              value={status.updated_at === null ? '—' : formatTimestamp(status.updated_at)}
              delta={{ text: t('accessStatus.kpi.updated_hint'), tone: 'neutral' }}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            {hasNamespaces ? (
              <NamespaceTimelinePanel namespaces={namespaces} windowHours={status.window_hours} />
            ) : (
              <Panel caption={t('accessStatus.vendors.caption')} testId="status-vendors">
                <Table className="rounded-none border-0" testId="status-vendors-table">
                  <THead>
                    <Tr interactive={false}>
                      <Th>{t('accessStatus.column.vendor')}</Th>
                      <Th num>{t('accessStatus.column.channels')}</Th>
                      <Th>{t('accessStatus.column.state')}</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {status.vendors.map((vendor) => (
                      <Tr key={vendor.vendor} data-testid={`status-vendor-row-${vendor.vendor}`}>
                        <Td emphasis heading>{vendor.vendor_display_name}</Td>
                        <Td num label={t('accessStatus.column.channels')}>
                          {vendor.healthy_channels}/{vendor.total_channels}
                        </Td>
                        <Td label={t('accessStatus.column.state')}>
                          <Chip tone={STATE_CHIP[vendor.state]}>{stateText(vendor.state, t)}</Chip>
                        </Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </Panel>
            )}

            {hasNamespaces ? (
              <DerivedEventsPanel namespaces={namespaces} />
            ) : (
              <LegacyEventsPanel status={status} />
            )}
          </div>
        </>
      ) : (
        <TableSkeleton rows={3} testId="status-pending" />
      )}
    </section>
  );
}

function formatAvailability(pct: number): string {
  return `${pct.toFixed(2)}%`;
}

function UptimeTimeline({
  history,
  dense = false,
  testId,
}: {
  readonly history: readonly PublicStatusBucketRecord[];
  readonly dense?: boolean;
  readonly testId?: string;
}): JSX.Element {
  const t = useT();
  return (
    <div
      className={cx('flex items-stretch gap-px', dense ? 'h-3' : 'h-6')}
      data-testid={testId}
    >
      {history.map((bucket) => {
        const noData = bucket.request_count === 0;
        const start = formatBucketTime(bucket.bucket_start);
        const end = formatBucketTime(bucket.bucket_end);
        return (
          <div
            key={bucket.bucket_start}
            data-state={noData ? 'none' : bucket.state}
            title={
              noData
                ? t('accessStatus.timeline.block_no_data', { start, end })
                : t('accessStatus.timeline.block_title', {
                    start,
                    end,
                    rate: bucket.success_rate_pct.toFixed(2),
                    count: bucket.request_count,
                  })
            }
            className={cx(
              'min-w-0 flex-1 rounded-sm',
              noData ? 'bg-line-soft' : TIMELINE_BLOCK[bucket.state],
            )}
          />
        );
      })}
    </div>
  );
}

function NamespaceTimelineRow({
  namespace,
}: {
  readonly namespace: PublicStatusNamespaceRecord;
}): JSX.Element {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const state = namespace.state ?? 'healthy';
  const skuCount = namespace.skus.length;

  return (
    <div
      className="flex flex-col gap-2 border-b border-line-soft px-4 py-3 last:border-b-0"
      data-testid={`status-namespace-row-${namespace.namespace_slug}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <StatusLamp tone={STATE_LAMP[state]} size="sm" className="self-center" />
        <span className="text-body font-medium text-paper">
          {namespace.namespace_display_name}
        </span>
        <span className="font-mono text-micro text-paper-low">{namespace.namespace_slug}</span>
        <span
          className="ml-auto font-mono text-body font-medium text-paper"
          data-testid={`status-namespace-availability-${namespace.namespace_slug}`}
        >
          {formatAvailability(namespace.availability_pct)}
        </span>
        <span className="text-micro text-paper-low">{t('accessStatus.timeline.uptime')}</span>
      </div>

      {namespace.history !== undefined && namespace.history.length > 0 ? (
        <>
          <UptimeTimeline
            history={namespace.history}
            testId={`status-namespace-timeline-${namespace.namespace_slug}`}
          />
          <div className="flex justify-between font-mono text-micro text-paper-faint">
            <span>{t('accessStatus.timeline.window_start')}</span>
            <span>{t('accessStatus.timeline.window_now')}</span>
          </div>
        </>
      ) : null}

      {skuCount > 0 ? (
        <div className="flex flex-col gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            aria-expanded={expanded}
            onClick={() => {
              setExpanded((open) => !open);
            }}
            data-testid={`status-namespace-skus-toggle-${namespace.namespace_slug}`}
          >
            {expanded
              ? t('accessStatus.timeline.skus_hide')
              : t('accessStatus.timeline.skus_show', { count: skuCount })}
          </Button>
          {expanded ? (
            <ul className="m-0 flex list-none flex-col gap-3 p-0 pl-4 max-md:pl-0">
              {namespace.skus.map((sku) => (
                <li key={sku.sku_id} data-testid={`status-sku-row-${sku.sku_id}`}>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="break-all font-mono text-dense text-paper-mid">
                      {sku.sku_name}
                    </span>
                    <span className="ml-auto font-mono text-dense text-paper-mid">
                      {formatAvailability(sku.availability_pct)}
                    </span>
                  </div>
                  {sku.history !== undefined && sku.history.length > 0 ? (
                    <UptimeTimeline
                      history={sku.history}
                      dense
                      testId={`status-sku-timeline-${sku.sku_id}`}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function NamespaceTimelinePanel({
  namespaces,
  windowHours,
}: {
  readonly namespaces: readonly PublicStatusNamespaceRecord[];
  readonly windowHours: number;
}): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={t('accessStatus.timeline.caption')}
      actions={
        <span className="text-micro text-paper-low">
          {t('accessStatus.timeline.window', { hours: windowHours })}
        </span>
      }
      testId="status-namespaces"
    >
      {namespaces.map((namespace) => (
        <NamespaceTimelineRow key={namespace.namespace_id} namespace={namespace} />
      ))}
    </Panel>
  );
}

function DerivedEventsPanel({
  namespaces,
}: {
  readonly namespaces: readonly PublicStatusNamespaceRecord[];
}): JSX.Element {
  const t = useT();
  const events = deriveNamespaceEvents(namespaces);
  return (
    <Panel caption={t('accessStatus.events.caption')} padded testId="status-events">
      {events.length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="status-events-empty">
          {t('accessStatus.events.empty')}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {events.map((event) => (
            <li
              key={event.key}
              data-testid={`status-event-${event.key}`}
              className="rounded border border-line-soft bg-well px-3 py-2"
            >
              <p className="m-0 truncate text-dense text-paper">{event.namespaceDisplayName}</p>
              <p className="m-0 mt-1 font-mono text-micro text-paper-low">
                {stateText(event.from, t)} → {stateText(event.to, t)} ·{' '}
                {formatTimestamp(event.at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function LegacyEventsPanel({
  status,
}: {
  readonly status: PublicStatusResponse;
}): JSX.Element {
  const t = useT();
  return (
    <Panel caption={t('accessStatus.events.caption')} padded testId="status-events">
      {status.recent_events.length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="status-events-empty">
          {t('accessStatus.events.empty')}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {status.recent_events.slice(0, 8).map((event) => (
            <li
              key={event.id}
              data-testid={`status-event-${event.id}`}
              className="rounded border border-line-soft bg-well px-3 py-2"
            >
              <p className="m-0 truncate text-dense text-paper">
                {event.channel_display_name}
              </p>
              <p className="m-0 mt-1 font-mono text-micro text-paper-low">
                {event.from_state ?? '—'} → {event.to_state} ·{' '}
                {formatTimestamp(event.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function HealthKpi({
  healthQ,
}: {
  readonly healthQ: UseQueryResult<{ readonly ok: true }>;
}): JSX.Element {
  const t = useT();
  const healthy = healthQ.data?.ok === true;
  return (
    <KpiCard
      testId="status-kpi-health"
      label={t('accessStatus.kpi.health')}
      tone={healthy ? 'ok' : healthQ.isError ? 'bad' : 'default'}
      value={healthy ? t('accessStatus.health.ok') : healthQ.isError ? t('accessStatus.health.down') : '—'}
      delta={{ text: t('accessStatus.kpi.health_hint'), tone: 'neutral' }}
    />
  );
}
